// Cio che e DEL GIN RUMMY nel tavolo: il dominio delle rotte del BFF, i posti, come si compone l'azione, come si disegna una carta, le frasi dei rifiuti. Il trasporto (socket,
// ticket, REST, seed, hook) e `lib/gamehost/`: qui se ne passano i PARAMETRI e basta (ADR-0235 §4).
import type { PostoPubblico } from '@/lib/gamehost/protocol';
import { creaTavoloBody } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import type { FacciaFrancese, SemeFrancese } from '@/lib/tavolo/carta';
import { etichettaAccantoAlBadge } from './etichetta';
import type { Carta, Mossa, MossaDiPersona } from './protocol';
import { rango, seme } from './regole';
import type { LivelloRobot } from './robot';

/** Il segmento di `/v1/games/<dominio>` (`limulo-app/gioco.js` di QGinRummy: `dominio: 'ginrummy'`). */
export const GINRUMMY_DOMINIO = 'ginrummy';

/** I posti che il gioco ammette (`POSTI_AMMESSI` di `apps/qginrummy-gameserver/src/regole.ts`): uno contro uno. */
export const POSTI_AMMESSI: readonly number[] = [2];
export const POSTI_GIN = 2;

// COPIA DICHIARATA da TMA#746 (briscola-orcb-8-tma) @ e823c650454b6adf11046bffc675774d6429937d: src/lib/briscola/gioco.ts, righe 39-47 (`creaTavoloBriscolaBody`), nella forma del gin.
// Cosa e diverso dall'originale: i posti sono FISSI a 2 (`POSTI_GIN`: il gin ha un solo formato), il corpo parte da quello del trasporto comune (`creaTavoloBody`) e `robot` si mette SOLO se il
// tavolo e casual (la briscola lo lascia al chiamante). `LivelloRobot` viene da `./robot`, la copia dei livelli.
/**
 * La richiesta di un tavolo nuovo del gin. Il livello dei robot esiste SOLO per un tavolo casual: per un privato («Invita un amico») il tipo lo vieta (`livello?: never`), e se arriva lo
 * stesso `creaTavoloGinRummyBody` non lo porta.
 */
export type RichiestaTavoloGin = { tipo: 'casual'; idempotencyKey: string; livello?: LivelloRobot } | { tipo: 'privato'; idempotencyKey: string; livello?: never };

/**
 * Il corpo di un tavolo nuovo: i campi di sempre (`tipo`, `posti`, `idempotencyKey`) e, SOLO se casual e con un livello scelto, `robot: { livello }`. Un tavolo coi robot e SEMPRE casual
 * (un invito non ha senso: nessun posto da riempire) e non e mai classificato: nessun campo `classificato`.
 */
export function creaTavoloGinRummyBody(a: RichiestaTavoloGin) {
  const base = creaTavoloBody({ tipo: a.tipo, posti: POSTI_GIN, idempotencyKey: a.idempotencyKey });
  return a.tipo === 'casual' && a.livello !== undefined ? { ...base, robot: { livello: a.livello } } : base;
}

/**
 * L'azione che parte sul filo: la mossa scelta con il posto aggiunto, e NIENT'ALTRO. Il server la confronta in forma canonica (chiavi in ordine) con le `mosse(vista)` del posto
 * (`puòAgire`): una chiave in piu, o in meno, e `ILLEGALE`. Funzione di MODULO: vale prima e dopo che `useTavoloGamehost` tenga l'ultima callback in un ref.
 */
export const componiAzione = (posto: number, da: MossaDiPersona): Mossa => ({ ...da, posto }) as Mossa;

/** La mossa del server (`mosse(vista)`) come la sceglie chi gioca, cioe senza il posto. */
export function senzaPosto(m: Mossa): MossaDiPersona {
  switch (m.t) {
    case 'SCARTA':
    case 'BUSSA':
    case 'BUSSA_SCARTI':
      return { t: m.t, carta: m.carta };
    default:
      return { t: m.t };
  }
}

/**
 * Il nome di un posto: il nickname di chi siede; un posto libero (o senza nome) e `libero`, la frase che passa chi chiama. Mai un ripiego su email o nome anagrafico (ADR-0238).
 * 🔴 ginrummy-orcr-13-testi: e il nome che si scrive ACCANTO al badge «bot» (ogni punto d'uso mette `BadgeBot` quando il posto e un robot), quindi per un robot non porta il «🤖 » iniziale del kit:
 * una sola icona (v. `etichetta.ts`). La sala d'attesa non passa di qui.
 */
export function nomeDelPosto(posti: readonly PostoPubblico[], p: number, libero: string): string {
  const pp = posti.find((x) => x.posto === p);
  return pp && pp.tipo !== 'libero' && pp.etichetta.trim() !== '' ? etichettaAccantoAlBadge(pp) : libero;
}

const SEMI: readonly SemeFrancese[] = ['c', 'd', 'h', 's'];

/** La faccia del modulo tavolo per una carta del gin (0..51): seme nell'ordine c d h s, rango 1..13 con l'asso a 1 (si disegna «A», come il rango 14). */
export function faccia(c: Carta): FacciaFrancese {
  return { mazzo: 'francese', rango: rango(c), seme: SEMI[seme(c)]! };
}

/**
 * Le frasi dei rifiuti (`lib/gamehost/rifiuti.ts`). Il game host del gin non manda MAI un `reason`: ogni rifiuto di `puòAgire` ha il suo `code` (FINITA, NON_DISTRIBUITO, ILLEGALE,
 * FUORI_TURNO), e dal core vengono STALE_SEQ e THROTTLED. Un codice che non e qui ha la frase generica.
 */
export const VOCI_RIFIUTO: VociRifiuto = {
  motivi: [],
  codici: ['FUORI_TURNO', 'ILLEGALE', 'STALE_SEQ', 'FINITA', 'NON_DISTRIBUITO', 'THROTTLED'],
};
